import { useState, useEffect } from 'react';
import { Copy, RefreshCw, Pencil } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { ChatCardView } from './ChatCard';
import { MessageTrace } from './MessageTrace';
import { Markdown } from '../mantastic/Markdown';
import type { ChatMessage, ChatCard, CommitDescriptor } from '../../types/chat';

function shortTime(epochSecs: number | undefined): string {
  if (!epochSecs) return '';
  return new Date(epochSecs * 1000).toLocaleTimeString(undefined, {
    hour: 'numeric',
    minute: '2-digit',
  });
}

interface ChatMessagesProps {
  messages: ChatMessage[];
  streamingText: string;
  isSending: boolean;
  error: string | null;
  onRegenerate: () => void;
  onEditAndResend: (messageId: string, newText: string) => void;
  onRetry: () => void;
  // Card confirm/dismiss state
  confirmingKey: string | null;
  results: Record<string, ChatCard>;
  dismissed: Record<string, boolean>;
  undoingKey: string | null;
  onConfirm: (key: string, card: ChatCard) => void;
  onDismiss: (key: string) => void;
  /** Returns an undo handler for a given card key+card, or undefined if not undoable */
  buildUndoHandler: (key: string, card: ChatCard) => (() => void) | undefined;
  /** Fires a display-card row's one-tap write (e.g. mark a task done). */
  onRowAction: (commit: CommitDescriptor) => Promise<void>;
  bottomRef: React.RefObject<HTMLDivElement>;
}

export function ChatMessages({
  messages,
  streamingText,
  isSending,
  error,
  onRegenerate,
  onEditAndResend,
  onRetry,
  confirmingKey,
  results,
  dismissed,
  undoingKey,
  onConfirm,
  onDismiss,
  buildUndoHandler,
  onRowAction,
  bottomRef,
}: ChatMessagesProps) {
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState('');

  // Fix 3: Reset edit state when messages are externally replaced (new chat, load thread, regenerate)
  useEffect(() => {
    setEditingId(null);
    setEditDraft('');
  }, [messages]);

  // Find the last assistant message id for Regenerate button
  const lastAssistantMsg = [...messages].reverse().find((m) => m.role === 'assistant');
  const lastAssistantId = lastAssistantMsg?.id;

  const handleCopy = (content: string) => {
    if (!navigator.clipboard) return;
    navigator.clipboard.writeText(content).then(() => {
      toast.success('Copied');
    }).catch(() => {
      toast.error('Could not copy');
    });
  };

  const startEdit = (msg: ChatMessage) => {
    setEditingId(msg.id);
    setEditDraft(msg.content);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditDraft('');
  };

  const saveEdit = (messageId: string) => {
    const trimmed = editDraft.trim();
    if (!trimmed) return;
    setEditingId(null);
    setEditDraft('');
    onEditAndResend(messageId, trimmed);
  };

  return (
    <>
      {messages.map((m) => (
        <div key={m.id} className={`group ${m.role === 'user' ? 'text-right' : ''}`}>
          {m.content && (
            <>
              {/* Action trail — above assistant message content.
                  Only for turns with no trace: a trace carries the same labels plus
                  what each step touched, so showing both would say it twice. Older
                  persisted messages still have actions and no trace, and keep this. */}
              {m.role === 'assistant' && !m.trace && m.actions && m.actions.length > 0 && (
                <div className="flex flex-col gap-0.5 mb-1">
                  {m.actions.map((action, i) => (
                    <span key={i} className="text-xs text-muted-foreground">· {action}</span>
                  ))}
                </div>
              )}

              {/* Edit mode for user messages */}
              {m.role === 'user' && editingId === m.id ? (
                <div className="inline-block w-full max-w-[90%] text-left">
                  <Textarea
                    value={editDraft}
                    onChange={(e) => setEditDraft(e.target.value)}
                    className="min-h-[60px] text-sm"
                    aria-label="Edit message"
                  />
                  <div className="mt-1 flex justify-end gap-1">
                    <Button size="sm" variant="ghost" className="h-6 px-2 text-xs" onClick={cancelEdit}>
                      Cancel
                    </Button>
                    <Button
                      size="sm"
                      className="h-6 px-2 text-xs"
                      onClick={() => saveEdit(m.id)}
                      disabled={isSending || !editDraft.trim()}
                    >
                      Save
                    </Button>
                  </div>
                </div>
              ) : (
                <div
                  className={`inline-block rounded-lg px-3 py-2 text-left text-sm ${
                    m.role === 'user' ? 'bg-primary text-primary-foreground' : 'bg-muted'
                  }`}
                >
                  {m.role === 'user' ? m.content : <Markdown>{m.content}</Markdown>}
                </div>
              )}

              {/* Where the answer came from. Sits directly under the reply it explains,
                  so an ungrounded answer cannot be read without seeing that it is one. */}
              {m.role === 'assistant' && editingId !== m.id && <MessageTrace trace={m.trace} />}

              {m.created_at && editingId !== m.id && (
                <div className="mt-0.5 text-[10px] text-muted-foreground">
                  {shortTime(m.created_at)}
                </div>
              )}

              {/* Message controls — hover reveal, below bubble */}
              {editingId !== m.id && (
                <div
                  className={`mt-0.5 flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity ${
                    m.role === 'user' ? 'justify-end' : 'justify-start'
                  }`}
                >
                  {m.role === 'assistant' && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6"
                      aria-label="Copy message"
                      onClick={() => handleCopy(m.content)}
                    >
                      <Copy className="h-3 w-3" />
                    </Button>
                  )}
                  {m.role === 'assistant' && m.id === lastAssistantId && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6"
                      aria-label="Regenerate response"
                      onClick={onRegenerate}
                      disabled={isSending}
                    >
                      <RefreshCw className="h-3 w-3" />
                    </Button>
                  )}
                  {m.role === 'user' && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6"
                      aria-label="Edit message"
                      onClick={() => startEdit(m)}
                      disabled={isSending}
                    >
                      <Pencil className="h-3 w-3" />
                    </Button>
                  )}
                </div>
              )}
            </>
          )}

          {m.cards.map((card, i) => {
            const key = `${m.id}:${i}`;
            if (dismissed[key]) return null;
            const shown = results[key] ?? card;
            // For historical messages (not in-session), don't attach confirm/cancel handlers
            const isLiveSession = m.id.startsWith('local-');
            const undoHandler = isLiveSession ? buildUndoHandler(key, shown) : undefined;
            return (
              <div key={key} className="mt-2">
                <ChatCardView
                  card={shown}
                  confirming={confirmingKey === key}
                  onConfirm={isLiveSession && shown.kind === 'confirm' ? () => onConfirm(key, shown) : undefined}
                  onCancel={
                    isLiveSession && shown.kind === 'confirm'
                      ? () => onDismiss(key)
                      : undefined
                  }
                  onUndo={undoHandler}
                  undoing={undoingKey === key}
                  onRowAction={isLiveSession ? onRowAction : undefined}
                />
              </div>
            );
          })}
        </div>
      ))}

      {isSending && streamingText && (
        <div className="inline-block rounded-lg bg-muted px-3 py-2 text-left text-sm motion-safe:animate-in motion-safe:fade-in-0 motion-safe:duration-small">
          <Markdown>{streamingText}</Markdown>
          {/* The caret marks a turn that is still streaming — it breathes on the
              ambient loop so a stalled stream is visible at a glance. */}
          <span
            aria-hidden
            className="ml-0.5 inline-block h-3.5 w-[2px] translate-y-[2px] rounded-full bg-brand-sky motion-safe:animate-breathe"
          />
        </div>
      )}

      {error && (
        <div className="flex items-center gap-2 rounded bg-destructive/10 px-3 py-2 text-sm text-destructive">
          <span className="flex-1">{error}</span>
          <Button variant="ghost" size="sm" className="h-6 px-2 text-xs" onClick={() => void onRetry()} disabled={isSending}>
            Retry
          </Button>
        </div>
      )}

      {/* Sentinel for auto-scroll */}
      <div ref={bottomRef} />
    </>
  );
}
